/**
 * News detail (`/news/[id]`) — a single editorial news article.
 *
 * Reads live `GET /news/:id` (full `body`, cover, attachments) via `useNewsItem`.
 * Reachable by tapping a live card in News → Cards (the list `/news` ships only
 * the excerpt; the full body lives here). Editorial news has no comments /
 * reactions (unlike feed posts at `/post/[id]`) — this is a read-only article.
 *
 * Note: `coverUrl` is used as-is (the API returns a resolved URL → first image →
 * null); attachment images go through `api.fileUrl(id)` like the post detail.
 */
import { useState } from 'react'
import { Image } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { Input, Stack, Text, TextArea, XStack, YStack } from 'tamagui'

import { api } from '../../../src/api/client'
import { useApp } from '../../../src/i18n'
import { useDocumentTitle } from '../../../src/lib/useDocumentTitle'
import { useNewsItem, useUpdateNews, useDeleteNews } from '../../../src/features/news/useNews'
import { useMe } from '../../../src/features/users/useUsers'
import { canManageNews } from '../../../src/auth/roles'
import { tone } from '../../../src/theme/tones'
import { timeAgo } from '../../../src/lib/format'
import { Avatar, Button, Card, Enter, Icon, Tag } from '../../../src/ui'
import { Page } from '../../../src/ui/Screen'
import { useVideoPlayer, VideoView } from 'expo-video'

/** Inline video attachment (expo-video — native + web). One hook per player ⇒ own component. */
function NewsVideo({ uri }: { uri: string }) {
  const player = useVideoPlayer(uri, (p) => {
    p.loop = false
  })
  return (
    <VideoView
      player={player}
      style={{ width: '100%', height: 280, borderRadius: 8, backgroundColor: '#000' }}
      contentFit="contain"
      allowsFullscreen
      nativeControls
    />
  )
}

export default function NewsDetailScreen() {
  const { t, l } = useApp()
  const router = useRouter()
  const { id } = useLocalSearchParams<{ id: string }>()
  const newsId = String(id ?? '')

  const { data: news, isLoading, isError } = useNewsItem(newsId)
  useDocumentTitle(news?.title ?? l('nav.news'))
  const { data: me } = useMe()

  // Правка/удаление новости — только модератор (admin/hr); бэкенд перепроверяет (403).
  const canManage = canManageNews(me)
  const [editing, setEditing] = useState(false)
  const [eTitle, setETitle] = useState('')
  const [eExcerpt, setEExcerpt] = useState('')
  const [eBody, setEBody] = useState('')
  const [confirmDel, setConfirmDel] = useState(false)
  const update = useUpdateNews(newsId, () => setEditing(false))
  const del = useDeleteNews(() => router.back())

  const startEdit = () => {
    if (!news) return
    setETitle(news.title)
    setEExcerpt(news.excerpt ?? '')
    setEBody(news.body ?? '')
    setEditing(true)
  }
  const canSave = eTitle.trim().length >= 3 && eBody.trim().length >= 1 && !update.isPending
  const saveEdit = () => {
    if (!canSave) return
    update.mutate({ title: eTitle.trim(), body: eBody.trim(), excerpt: eExcerpt.trim() })
  }

  const back = (
    <XStack
      onPress={() => router.back()}
      cursor="pointer"
      animation="quick"
      alignItems="center"
      gap={6}
      alignSelf="flex-start"
      paddingVertical={4}
      hoverStyle={{ opacity: 0.8 }}
    >
      <Icon name="arrow-left" size={16} color="$primary" />
      <Text fontSize={13} fontWeight="600" color="$primary">
        {l('news.back')}
      </Text>
    </XStack>
  )

  return (
    <Page maxWidth={760}>
      {back}

      {isLoading ? (
        <Card padding={40} alignItems="center">
          <Text fontSize={13} color="$muted">
            …
          </Text>
        </Card>
      ) : isError || !news ? (
        <Card padding={40} alignItems="center" gap={6}>
          <Text fontSize={14} fontWeight="700" color="$color">
            {l('news.notFound')}
          </Text>
        </Card>
      ) : (
        <Card overflow="hidden" padding={0}>
          {/* Article blocks cascade in: cover/title → meta → body → attachments. */}
          <Enter index={0}>
            {news.coverUrl ? (
              <Image source={{ uri: api.assetUrl(news.coverUrl) }} style={{ width: '100%', height: 240 }} resizeMode="cover" />
            ) : (
              <Stack height={10} backgroundColor={tone(news.isPinned ? 'gold' : 'purple').solid} />
            )}
          </Enter>

          <YStack padding={24} gap={14}>
            <Enter index={1}>
              <XStack alignItems="center" gap={8} flexWrap="wrap">
                <Tag tone={news.isPinned ? 'gold' : 'purple'}>{l('nav.news')}</Tag>
                {news.isPinned && (
                  <XStack alignItems="center" gap={4}>
                    <Icon name="pin" size={13} color="$tertiary" />
                    <Text fontSize={11} fontWeight="600" color="$muted">
                      {l('feed.pinned')}
                    </Text>
                  </XStack>
                )}
              </XStack>
            </Enter>

            {!editing && (
              <Enter index={0}>
                <Text fontSize={24} fontWeight="800" color="$color" lineHeight={31}>
                  {news.title}
                </Text>
              </Enter>
            )}

            <Enter index={1}>
              <XStack alignItems="center" gap={10}>
                <Avatar name={news.author?.fullName || '—'} seed={news.author?.id ?? news.id} size="sm" uri={api.assetUrl(news.author?.avatarUrl)} />
                <Text fontSize={12} color="$muted" numberOfLines={1}>
                  {news.author?.fullName ?? ''}
                  {timeAgo(news.publishedAt ?? news.createdAt) ? ` · ${timeAgo(news.publishedAt ?? news.createdAt)}` : ''}
                </Text>
              </XStack>
            </Enter>

            {!editing && !!news.excerpt && (
              <Enter index={2}>
                <Text fontSize={16} fontWeight="600" color="$color" lineHeight={24}>
                  {news.excerpt}
                </Text>
              </Enter>
            )}

            {!editing && !!news.body && (
              <Enter index={2}>
                <Text fontSize={15} color="$color" lineHeight={24}>
                  {news.body}
                </Text>
              </Enter>
            )}

            {/* Модерация (admin/hr): правка формы или кнопки Изменить/Удалить. */}
            {canManage && (
              <Enter index={2}>
                {editing ? (
                  <YStack gap={8}>
                    <Input
                      value={eTitle}
                      onChangeText={setETitle}
                      placeholder={t({ ru: 'Заголовок', en: 'Title' })}
                      placeholderTextColor="$tertiary"
                      height={44}
                      fontSize={18}
                      fontWeight="700"
                      color="$color"
                      backgroundColor="$bgLayout"
                      borderWidth={1}
                      borderColor="$borderColor"
                      borderRadius={7}
                      paddingHorizontal={11}
                      focusStyle={{ borderColor: '$primary' }}
                    />
                    <Input
                      value={eExcerpt}
                      onChangeText={setEExcerpt}
                      placeholder={t({ ru: 'Короткий анонс (необязательно)', en: 'Short excerpt (optional)' })}
                      placeholderTextColor="$tertiary"
                      height={40}
                      fontSize={14}
                      color="$color"
                      backgroundColor="$bgLayout"
                      borderWidth={1}
                      borderColor="$borderColor"
                      borderRadius={7}
                      paddingHorizontal={11}
                      focusStyle={{ borderColor: '$primary' }}
                    />
                    <TextArea
                      value={eBody}
                      onChangeText={setEBody}
                      placeholder={t({ ru: 'Текст новости', en: 'Article body' })}
                      placeholderTextColor="$tertiary"
                      minHeight={140}
                      fontSize={15}
                      color="$color"
                      backgroundColor="$bgLayout"
                      borderWidth={1}
                      borderColor="$borderColor"
                      borderRadius={7}
                      paddingHorizontal={11}
                      paddingVertical={9}
                      focusStyle={{ borderColor: '$primary' }}
                    />
                    <XStack gap={8} justifyContent="flex-end">
                      <Button onPress={() => setEditing(false)}>{t({ ru: 'Отмена', en: 'Cancel' })}</Button>
                      <Button
                        variant="primary"
                        onPress={saveEdit}
                        opacity={canSave ? 1 : 0.5}
                        cursor={canSave ? 'pointer' : 'default'}
                      >
                        {update.isPending ? t({ ru: 'Сохраняю…', en: 'Saving…' }) : t({ ru: 'Сохранить', en: 'Save' })}
                      </Button>
                    </XStack>
                  </YStack>
                ) : (
                  <XStack gap={8} alignItems="center" flexWrap="wrap">
                    <Button icon={<Icon name="pencil" size={14} color="$muted" />} onPress={startEdit}>
                      {t({ ru: 'Изменить', en: 'Edit' })}
                    </Button>
                    {confirmDel ? (
                      <>
                        <Button onPress={() => setConfirmDel(false)}>{t({ ru: 'Отмена', en: 'Cancel' })}</Button>
                        <Button
                          variant="primary"
                          onPress={() => del.mutate(news.id)}
                          opacity={del.isPending ? 0.6 : 1}
                          icon={<Icon name="x" size={14} color="#fff" />}
                        >
                          {t({ ru: 'Удалить?', en: 'Delete?' })}
                        </Button>
                      </>
                    ) : (
                      <Button icon={<Icon name="x" size={14} color="$muted" />} onPress={() => setConfirmDel(true)}>
                        {t({ ru: 'Удалить', en: 'Delete' })}
                      </Button>
                    )}
                  </XStack>
                )}
              </Enter>
            )}

            {!editing && news.attachments && news.attachments.length > 0 && (
              <Enter index={3} gap={10} marginTop={4}>
                {news.attachments.map((a) =>
                  a.mime?.startsWith('image/') ? (
                    <Image
                      key={a.id}
                      source={{ uri: api.fileUrl(a.id) }}
                      style={{ width: '100%', height: 280, borderRadius: 8 }}
                      resizeMode="cover"
                    />
                  ) : a.mime?.startsWith('video/') ? (
                    <NewsVideo key={a.id} uri={api.fileUrl(a.id)} />
                  ) : (
                    <XStack
                      key={a.id}
                      alignItems="center"
                      gap={8}
                      padding={10}
                      borderRadius={7}
                      borderWidth={1}
                      borderColor="$borderColor"
                      backgroundColor="$bgLayout"
                    >
                      <Icon name="paperclip" size={15} color="$muted" />
                      <Text fontSize={13} color="$color" flex={1} numberOfLines={1}>
                        {a.name}
                      </Text>
                    </XStack>
                  ),
                )}
              </Enter>
            )}
          </YStack>
        </Card>
      )}

      <Stack height={8} />
    </Page>
  )
}
